import React, { memo } from 'react';
import equal from 'fast-deep-equal';
import { Calendar, Check } from 'lucide-react';

import { DATE_FILTER } from '@repo/types';
import { Button } from '@repo/ui/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@repo/ui/components/ui/dropdown-menu';
import { cn } from '@repo/ui/lib/utils';

import { ListArticlesByBrandParams } from '@/hooks/api/article/model';

export const DATE_FILTER_OPTIONS = [
  {
    label: 'Today',
    value: DATE_FILTER.TODAY,
  },
  {
    label: 'Last 3 days',
    value: DATE_FILTER.THREE_DAYS,
  },
  {
    label: 'This week',
    value: DATE_FILTER.THIS_WEEK,
  },
  {
    label: 'This month',
    value: DATE_FILTER.THIS_MONTH,
  },
  {
    label: 'All time',
    value: DATE_FILTER.ALL_TIME,
  },
];

interface FeedDateFilterProps {
  options?: typeof DATE_FILTER_OPTIONS;
  dateFilter?: DATE_FILTER;
  onFilterChange: (
    newFilters: Pick<ListArticlesByBrandParams, 'dateFilter'>,
  ) => void;
  className?: string;
}

const PureFeedDateFilter: React.FC<FeedDateFilterProps> = ({
  options = DATE_FILTER_OPTIONS,
  dateFilter,
  onFilterChange,
  className,
}) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          className={cn(
            'h-auto p-0.5 hover:bg-transparent hover:text-inherit [&_svg]:size-[18px] cred-md:[&_svg]:size-5 cred-lg:[&_svg]:size-[23px] cred-xl:[&_svg]:size-[30px]',
            className,
          )}
        >
          <span className="sr-only">Filter</span>
          <Calendar />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="min-w-32 bg-[#F0F2F5] p-0 cred-md:min-w-40 cred-lg:min-w-48 cred-xl:min-w-56"
      >
        {options.map((option) => (
          <DropdownMenuItem
            key={option.value}
            onSelect={() => onFilterChange({ dateFilter: option.value })}
            className={cn(
              'h-auto cursor-pointer gap-1 rounded-b-none py-1.5 pl-2 pr-3 text-black focus:text-black cred-md:gap-1.5 cred-md:py-2.5 cred-md:pl-3 cred-md:pr-4 cred-lg:gap-1.5 cred-lg:py-2.5 cred-lg:pl-3 cred-lg:pr-4 cred-xl:gap-2 cred-xl:py-3 cred-xl:pl-4 cred-xl:pr-5',
              dateFilter === option.value
                ? 'font-semibold focus:bg-transparent'
                : 'focus:bg-[#F1F5F9] active:bg-[#E5F2FF]',
            )}
          >
            <Check
              className={cn(
                'stroke-[2.8px] transition-opacity duration-300',
                dateFilter === option.value ? 'opacity-100' : 'opacity-0',
              )}
            />
            <span className="text-sm leading-tight cred-md:text-base cred-lg:text-base cred-xl:text-lg">
              {option.label}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const FeedDateFilter = memo(
  PureFeedDateFilter,
  (prevProps, nextProps) => {
    return (
      equal(prevProps.options, nextProps.options) &&
      prevProps.dateFilter === nextProps.dateFilter &&
      prevProps.className === nextProps.className &&
      prevProps.onFilterChange === nextProps.onFilterChange
    );
  },
);
